import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useContext,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import Sheet from '@amzn/meridian/sheet';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Box from '@amzn/meridian/box';
import RadioButton from '@amzn/meridian/radio-button';
import closeMediumTokens from '@amzn/meridian-tokens/base/icon/close-medium';
import filterTokens from '@amzn/meridian-tokens/base/icon/filter';
import Heading from '@amzn/meridian/heading';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import {
  OutlierType,
  FilterValues,
  UniversalFilterValue,
  FilterKey,
  FilterValueType,
} from '../types/types';
import { getOutlierI18nKey } from '../helpers';

import {
  createFilterChangeHandler,
  getWeeksListForFilter,
  getWeekRangeFromWeekList,
} from '../filterHelpers';
import { OutlierTimeOption } from '@amzn/aet-dynamo-kit';
import { StateContext } from '../../../GlobalStateStore';

interface OMOutlierFilterProps {
  title: string;
  filters: FilterValues;
  tmList: string[];
  categories: OutlierType[];
  onFilterChange: (newFilters: Partial<FilterValues>) => void;
  onClearFilters: () => void;
}

const OMOutlierFilter: React.FC<OMOutlierFilterProps> = ({
  title,
  filters,
  categories,
  tmList,
  onFilterChange,
  onClearFilters,
}) => {
  const [open, setOpen] = useState(false);
  const [viewDataBySelection, setViewDataBySelection] = useState(
    filters.viewDataBy
  );

  const intl = useIntl();

  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { isTm, isOm, requesterUserID, requesterUsername } =
    inspireOptions.data;

  const commonClickstreamPattern = {
    'data-csa-c-profile-id': requesterUserID,
    'data-csa-c-username': requesterUsername,
    'data-csa-c-is-TM': isTm,
    'data-csa-c-is-OM': isOm,
  };

  const onClickOpen = useCallback(() => setOpen(true), []);
  const onClose = useCallback(() => setOpen(false), []);

  const handleFilterChange = useCallback(
    createFilterChangeHandler(onFilterChange),
    [onFilterChange]
  );

  const weekList = useMemo(() => getWeeksListForFilter(), []);
  const weekListRange = useMemo(
    () => weekList && getWeekRangeFromWeekList(weekList),
    [weekList]
  );

  const renderWeekSelectionOptions = (weekList: string[]) => {
    return (
      <Select
        value={filters.timeOption}
        onChange={(value: FilterValueType[typeof FilterKey.TIME_OPTION]) => {
          onWeeklyTimePeriodSelected(value);
        }}
        width={240}
      >
        {weekList.map(week => (
          <SelectOption key={week} label={week} value={week} />
        ))}
      </Select>
    );
  };

  const renderReadOnlyWeekList = (weekList: string[]) => {
    return (
      <Text>
        {weekList.map(week => (
          <li>{week}</li>
        ))}
      </Text>
    );
  };

  const renderTimeOptionSelections = (viewDataByTimeOption: string) => {
    return (
      <>
        <Text color='secondary'>
          <FormattedMessage
            id='cs_perfportal_filter_for_the_following_weeks'
            defaultMessage='For the following weeks'
          />
        </Text>
        {viewDataByTimeOption === OutlierTimeOption.WEEKLY
          ? renderWeekSelectionOptions(weekList)
          : renderReadOnlyWeekList(weekList)}
      </>
    );
  };

  const renderedTimeOption = useMemo(
    () => renderTimeOptionSelections(viewDataBySelection),
    [viewDataBySelection]
  );

  const onWeeklyTimePeriodSelected = async (selectedTimePeriod?: string) => {
    setViewDataBySelection(OutlierTimeOption.WEEKLY);
    handleFilterChange({
      [FilterKey.TIME_OPTION]: selectedTimePeriod ?? filters.timeOption,
      [FilterKey.TIME_OPTIONS]: selectedTimePeriod
        ? [selectedTimePeriod]
        : [filters.timeOption],
      [FilterKey.VIEW_DATA_BY]: OutlierTimeOption.WEEKLY,
    });
  };

  const onMonthlyTimePeriodSelected = async (selectedWeekRange: string[]) => {
    setViewDataBySelection(OutlierTimeOption.MONTHLY);
    handleFilterChange({
      [FilterKey.TIME_OPTIONS]: selectedWeekRange,
      [FilterKey.VIEW_DATA_BY]: OutlierTimeOption.MONTHLY,
    });
  };

  const preselectedCategoryExists = categories.some(
    category => category === filters.category
  );
  const preselectedTMExists = tmList.some(category => category === filters.tm);

  return (
    <Row alignmentHorizontal='justify' alignmentVertical='center'>
      <Column spacingInset='none none none 400'>
        <Text type='h50'>{title}</Text>
      </Column>
      <Button type='icon' onClick={onClickOpen}>
        <Icon tokens={filterTokens}>
          <FormattedMessage
            id='cs_perfportal_filter_button'
            defaultMessage='Filter'
          />
        </Icon>
      </Button>
      <Sheet type='overlay' open={open} onClose={onClose}>
        <Row alignmentVertical='center' alignmentHorizontal='justify'>
          <Heading level={3}>
            <FormattedMessage
              id='cs_perfportal_filter_outliers_title'
              defaultMessage='Filter Outliers'
            />
          </Heading>
          <Button onClick={onClose} type='icon'>
            <Icon tokens={closeMediumTokens}>
              <FormattedMessage
                id='cs_perfportal_close_sheet'
                defaultMessage='Close sheet'
              />
            </Icon>
          </Button>
        </Row>
        <Column spacing='medium' spacingInset='400 none none none'>
          <Box spacingInset='200 none'>
            <Column spacing='350'>
              <Text color='secondary'>
                <FormattedMessage
                  id='cs_perfportal_filter_view_data_by'
                  defaultMessage='View data by'
                />
              </Text>
              <RadioButton
                checked={viewDataBySelection === OutlierTimeOption.WEEKLY}
                onChange={onWeeklyTimePeriodSelected}
                name='viewDataBy'
                value={filters.timeOption}
              >
                <FormattedMessage
                  id='cs_perfportal_weeks'
                  defaultMessage='Weeks'
                />
              </RadioButton>
              <RadioButton
                checked={viewDataBySelection === OutlierTimeOption.MONTHLY}
                onChange={onMonthlyTimePeriodSelected}
                name='viewDataBy'
                value={weekListRange} // Monthly time period has a readonly fixed week range to be applied
              >
                <FormattedMessage
                  id='cs_perfportal_month'
                  defaultMessage='Month'
                />
              </RadioButton>
            </Column>
          </Box>

          <Box spacingInset='200 none'>
            <Column spacing='small'>{renderedTimeOption}</Column>
          </Box>
          <Box spacingInset='200 none'>
            <Column spacing='small'>
              <Text color='secondary'>
                <FormattedMessage
                  id='cs_perfportal_outlier_filter_category_label'
                  defaultMessage='Outlier Category'
                />
              </Text>
              <Select
                value={
                  preselectedCategoryExists
                    ? filters.category
                    : UniversalFilterValue.ALL
                }
                onChange={(value: FilterValueType[typeof FilterKey.CATEGORY]) =>
                  handleFilterChange(FilterKey.CATEGORY, value)
                }
                width={240}
              >
                <SelectOption
                  label={intl.formatMessage({
                    id: 'cs_perfportal_filter_all',
                    defaultMessage: 'All',
                  })}
                  value={UniversalFilterValue.ALL}
                  data-csa-c-type='select'
                  data-csa-c-slot-id='om-outlier-management-filter-outliers-category-select-slot'
                  data-csa-c-tag1='Coaching'
                  data-csa-c-tag2='Outlier Metrics'
                  data-csa-c-tag3='Filter Outliers'
                  data-csa-c-tag4={'All'}
                  {...commonClickstreamPattern}
                />
                {categories.map(category => (
                  <SelectOption
                    key={category}
                    label={intl.formatMessage({
                      id: getOutlierI18nKey(category),
                      defaultMessage: category,
                    })}
                    value={category}
                    data-csa-c-type='select'
                    data-csa-c-slot-id='om-outlier-management-filter-outliers-category-select-slot'
                    data-csa-c-tag1='Coaching'
                    data-csa-c-tag2='Outlier Metrics'
                    data-csa-c-tag3='Filter Outliers'
                    data-csa-c-tag4={category}
                    {...commonClickstreamPattern}
                  />
                ))}
              </Select>
            </Column>
          </Box>
          <Box spacingInset='200 none'>
            <Column spacing='small'>
              <Text color='secondary'>
                <FormattedMessage id='cs_perfportal_tm' defaultMessage='TM' />
              </Text>
              <Select
                value={
                  preselectedTMExists ? filters.tm : UniversalFilterValue.ALL
                }
                onChange={(value: FilterValueType[typeof FilterKey.TM]) =>
                  handleFilterChange(FilterKey.TM, value)
                }
                width={240}
              >
                <SelectOption
                  label={intl.formatMessage({
                    id: 'cs_perfportal_filter_all',
                    defaultMessage: 'All',
                  })}
                  value={UniversalFilterValue.ALL}
                  data-csa-c-type='select'
                  data-csa-c-slot-id='om-outlier-management-filter-outliers-tm-select-slot'
                  data-csa-c-tag1='Coaching'
                  data-csa-c-tag2='Outlier Metrics'
                  data-csa-c-tag3='Filter Outliers'
                  data-csa-c-tag4={'All'}
                  {...commonClickstreamPattern}
                />
                {tmList.map(tm => (
                  <SelectOption
                    key={tm}
                    label={tm}
                    value={tm}
                    data-csa-c-type='select'
                    data-csa-c-slot-id='om-outlier-management-filter-outliers-tm-select-slot'
                    data-csa-c-tag1='Coaching'
                    data-csa-c-tag2='Outlier Metrics'
                    data-csa-c-tag3='Filter Outliers'
                    data-csa-c-tag4={tm}
                    {...commonClickstreamPattern}
                  />
                ))}
              </Select>
            </Column>
          </Box>

          <Row
            width='100%'
            widths='fill'
            alignmentHorizontal='center'
            spacing='medium'
            spacingInset='200 none'
          >
            <Button
              size='medium'
              onClick={() => {
                onClearFilters();
                onClose();
              }}
              type='secondary'
            >
              <FormattedMessage
                id='cs_perfportal_filter_reset'
                defaultMessage='Reset filters'
              />
            </Button>
          </Row>
        </Column>
      </Sheet>
    </Row>
  );
};

export default OMOutlierFilter;
